/* eslint-disable @typescript-eslint/no-explicit-any */
/* ────────────────────────────────────────────────────────────────────────
   salaryStructure.calc.ts

   Pure, unit-testable preview maths used by the sticky Summary panel and the
   view screen. One function per template. No React, no side effects, and NO
   second calculation engine — these only mirror the existing field meaning
   (Basic = % of CTC, HRA = % of Basic, allowances fixed or % of Basic).
   ──────────────────────────────────────────────────────────────────────── */

const num = (v: any): number => {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(n) ? Number(n) : 0;
};

// Indian-format ₹ used across the module.
export const formatINR = (value: number, withDecimals = false): string => {
  const n = Number.isFinite(value) ? value : 0;
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: withDecimals ? 2 : 0,
    maximumFractionDigits: withDecimals ? 2 : 0,
  })}`;
};

export const monthsLabel = (months?: number | null): string => {
  const m = num(months);
  if (!m) return "—";
  return `${m} month${m === 1 ? "" : "s"}`;
};

export interface EmployeePreview {
  ctc: number;
  monthlyBasic: number;
  monthlyHra: number;
  monthlyConveyance: number;
  monthlyMedical: number;
  monthlySpecial: number;
  monthlyGross: number;
  pfEmployee: number;
  professionalTax: number;
  netTakeHome: number;
  employerCostMonthly: number;
}

// Resolve a fixed/percentage allowance to a MONTHLY rupee figure.
const monthlyAllowance = (amount: any, isPercentage: any, monthlyBasic: number, annual = true): number => {
  const v = num(amount);
  if (isPercentage) return (monthlyBasic * v) / 100;
  return annual ? v / 12 : v; // fixed amounts are stored annually unless noted
};

export const computeEmployeePreview = (values: any, testCtc: number): EmployeePreview => {
  const ctc = num(testCtc);
  const monthlyCtc = ctc / 12;
  const monthlyBasic = (monthlyCtc * num(values.basicSalaryPercentage)) / 100;
  const monthlyHra = (monthlyBasic * num(values.hraPercentage)) / 100; // HRA = % of Basic
  const monthlyConveyance = monthlyAllowance(values.conveyanceAllowance, values.conveyanceIsPercentage, monthlyBasic);
  const monthlyMedical = monthlyAllowance(values.medicalAllowance, values.medicalAllowanceIsPercentage, monthlyBasic);
  const monthlySpecial = Math.max(0, monthlyCtc - (monthlyBasic + monthlyHra + monthlyConveyance + monthlyMedical));
  const monthlyGross = monthlyBasic + monthlyHra + monthlyConveyance + monthlyMedical + monthlySpecial;

  const pfBase = values.pfWageCeilingApplicable ? Math.min(monthlyBasic, 15000) : monthlyBasic;
  const pfEmployee = values.pfApplicable ? (pfBase * num(values.providentFundEmployeePercentage)) / 100 : 0;
  const pfEmployer = values.pfApplicable ? (pfBase * num(values.providentFundEmployerPercentage)) / 100 : 0;
  const professionalTax = values.profTaxApplicable ? num(values.taxOnProfession) : 0;

  return {
    ctc,
    monthlyBasic,
    monthlyHra,
    monthlyConveyance,
    monthlyMedical,
    monthlySpecial,
    monthlyGross,
    pfEmployee,
    professionalTax,
    netTakeHome: monthlyGross - pfEmployee - professionalTax,
    employerCostMonthly: monthlyGross + pfEmployer,
  };
};

export interface ContractPreview {
  isGst: boolean;
  annualContractValue: number;
  monthlyPayout: number;
  gstPercentage: number;
  gstAmount: number;
  invoiceTotal: number;
  tdsRate: number;
  estimatedMonthlyTds: number;
  estimatedNetPayout: number;
  annualTds: number;
}
export const computeContractPreview = (values: any): ContractPreview => {
  const monthlyPayout = num(values.monthlyPayout);
  const tdsRate = num(values.tdsRate);
  const isGst = values.contractType === "GST";
  const gstPercentage = isGst ? num(values.gstPercentage) : 0;
  const gstAmount = (monthlyPayout * gstPercentage) / 100;
  const invoiceTotal = monthlyPayout + gstAmount;
  // TDS is deducted on the pre-GST payout.
  const estimatedMonthlyTds = (monthlyPayout * tdsRate) / 100;
  return {
    isGst,
    annualContractValue: num(values.contractValue),
    monthlyPayout,
    gstPercentage,
    gstAmount,
    invoiceTotal,
    tdsRate,
    estimatedMonthlyTds,
    estimatedNetPayout: invoiceTotal - estimatedMonthlyTds,
    annualTds: estimatedMonthlyTds * 12,
  };
};

export interface TraineePreview {
  monthlyStipend: number;
  months: number;
  totalCost: number;
}
export const computeTraineePreview = (values: any): TraineePreview => {
  const monthlyStipend = num(values.monthlyStipend);
  const months = num(values.periodMonths);
  return { monthlyStipend, months, totalCost: monthlyStipend * months };
};
